import * as React from "react";
import { useEffect, useRef, useState } from "react";
import {
    DataGridPremium,
    GRID_ROOT_GROUP_ID,
    useGridApiRef,
    useKeepGroupedColumnsHidden,
    gridSortedRowIdsSelector,
} from "@mui/x-data-grid-premium";
import { useForecastReviewContext } from "../Contexts/forecastReviewContext";
import dayjs from "dayjs";
import { Box, Button, IconButton, LinearProgress, Stack, Tooltip, Typography } from "@mui/material";
import { PausedIndicator } from "./PausedIndicator";
import { isInt } from "../Utilities/utilities.js";
import { Alert } from "@mui/lab";
import { usePrompt } from "../Utilities/usePrompt.ts";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import InfoIcon from "@mui/icons-material/Info";
import { useProductsContext } from "../Contexts/productsContext";
import { useDistributorsContext } from "../Contexts/distributorsContext.jsx";
import YearOverYearSalesDetailPanel from "./YearOverYearSalesDetailPanel";
import PropTypes from 'prop-types';

const enableHistoricalSales = process.env.REACT_APP_ENABLE_HISTORICAL_SALES === 'true';
const enablePauseIndicator = process.env.REACT_APP_ENABLE_PAUSE_INDICATOR === 'true';

export const ForecastReviewGrid = ({
    editableWeeksStartIndex,
    editableWeeksEndIndex,
    startWeek,
    endWeek,
    isHistorical,
    filterByFulfillmentMethodId,
    hideSubmit,
    hideAlerts,
    height,
}) => {
    const {
        forecastReviewItems,
        setForecastReviewItems,
        SaveForecastAdjustmentChanges,
        isReady: areForecastReviewItemsReady,
        isReadOnly,
        error,
        getFormattedOriginalForecastReviewItems,
        edited,
        setEdited,
    } = useForecastReviewContext();
    const { selectedProducts, setSelectedProducts } = useProductsContext();
    const { selectedDistributors } = useDistributorsContext();
    const [sending, setSending] = useState(false);
    const [expandedRows, setExpandedRows] = useState([]);
    // Prior-year depletions fetched lazily per expanded row: { [rowId]: { loading, error, depletions } }
    const [historicalCache, setHistoricalCache] = useState({});
    const expandedGroups = useRef([]);

    // The current displayed week window as OB_Week Saturdays (date-only so the backend's inclusive
    // DtmWeek filter matches exactly). Review page shows weeks [0..endWeek-1]; Report page [-8..-1].
    const historicalWeekStart = dayjs().add(startWeek, "week").endOf("week").format("YYYY-MM-DD");
    const historicalWeekEnd = dayjs().add(endWeek - 1, "week").endOf("week").format("YYYY-MM-DD");
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({
        "product.name": false,
        depletions0: false,
        depletions1: false,
        depletions2: false,
        "product.brandFamily": false,
        "product.brand": false,
        "__detail_panel_toggle__": false,
    });

    function isRealGroup(groupId) {
        return typeof groupId === "string" && groupId.includes("auto-generated");
    }

    function expandChildren(apiRef, groups) {
        if (groups && groups.length > 0) {
            groups.forEach((groupId) => {
                if (isRealGroup(groupId)) {
                    apiRef.current.setRowChildrenExpansion(groupId, true);

                    expandChildren(apiRef, apiRef.current.getRowNode(groupId).children);
                }
            });
        }
    }

    function matchForecast(params, date) {
        return params.row.forecastAmounts?.find((forecastAmount) => dayjs(forecastAmount.weekEndDate).endOf("week").isSame(date, "day"),);
    }

    function matchDepletion(params, date) {
        return params.row.depletions?.find((depetion) => dayjs(depetion.month).endOf("week").isSame(date, "day"),);
    }

    function showHideDepletions() {
        setColumnVisibilityModel((prevState) => ({
            "product.name": false,
            depletions0: !prevState.depletions0,
            depletions1: !prevState.depletions1,
            depletions2: !prevState.depletions2,
            "product.brandFamily": false,
            "product.brand": false,
        }));
    }

    usePrompt("You have unsaved adjustments. Are you sure you want to leave?", edited,);

    let columnDefinitions = [];
    let columnGroupingModel = [];
    const apiRef = useGridApiRef();

    useEffect(() => {
        apiRef.current.subscribeEvent("rowExpansionChange", (node) => {
            if (node.childrenExpanded) {
                expandedGroups.current.push(node.id);
            } else {
                expandedGroups.current.pop(node.id);
            }
        });

        expandChildren(apiRef, apiRef.current.getRowNode(GRID_ROOT_GROUP_ID).children,);
    }, [apiRef]);

    useEffect(() => {
        const selectedSKUs = selectedProducts?.map(selectedProduct => selectedProduct.sku);
        if (selectedSKUs?.length > 0) {
            setForecastReviewItems(getFormattedOriginalForecastReviewItems().filter(forecastReviewItem => selectedSKUs.includes(forecastReviewItem.product.sku)));
        } else {
            setForecastReviewItems(getFormattedOriginalForecastReviewItems());
        }
    }, [selectedProducts, setSelectedProducts]);

    const initialState = useKeepGroupedColumnsHidden({
        apiRef, initialState: {
            pinnedColumns: {
                left: [...(enablePauseIndicator ? ["paused"] : []), "product.name", "__row_group_by_columns_group__",
                    ...(enableHistoricalSales ? ["last4weeks"] : []),
                    "distributorSapId", "product.sku", "customerPartNumber"],
            }, rowGrouping: {
                model: ["product.brandFamily", "product.brand"],
            },
        },
    });

    const displayedRows = filterByFulfillmentMethodId != null
        ? forecastReviewItems.filter(item => item.fulfillmentMethodId === filterByFulfillmentMethodId)
        : forecastReviewItems;

    // Lazily fetch prior-year depletions for each newly expanded row (mirrors YearOverYearSalesDetailPanel).
    useEffect(() => {
        if (!enableHistoricalSales) return;
        const controllers = [];
        expandedRows.forEach((rowId) => {
            if (historicalCache[rowId]) return;
            const row = displayedRows.find((r) => r.id === rowId);
            if (!row?.product?.sku) return;

            const controller = new AbortController();
            controllers.push(controller);
            setHistoricalCache((prev) => ({ ...prev, [rowId]: { loading: true } }));

            fetch(
                `/forecastreview/historicaldepletion?distributorId=${row.distributorId}` +
                `&sku=${encodeURIComponent(row.product.sku)}` +
                `&weekStart=${historicalWeekStart}&weekEnd=${historicalWeekEnd}`,
                { signal: controller.signal }
            )
                .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
                .then((data) =>
                    setHistoricalCache((prev) => ({
                        ...prev,
                        [rowId]: { loading: false, depletions: data.depletions ?? [] },
                    }))
                )
                .catch((err) => {
                    if (err?.name === "AbortError") return;
                    setHistoricalCache((prev) => ({
                        ...prev,
                        [rowId]: { loading: false, error: true, depletions: [] },
                    }));
                });
        });
        return () => controllers.forEach((c) => c.abort());
    }, [expandedRows]);

    // Interleave a read-only "Prior Year" row directly after each expanded product row. It shares the
    // product's grouping keys so it nests under the same brand, and its depletions (prior-year actuals
    // re-stamped to the current weeks) render under the D column on the Report page and under the
    // F column on the Review page. The row shows no week labels of its own —
    // the shared week-ending column headers already cover it. panelSku/panelDistributorId let the
    // Historical Sales detail panel attach to THIS row, so the panel renders below the Prior Year row.
    const rowsWithHistorical = React.useMemo(() => {
        if (!enableHistoricalSales) return displayedRows;
        const out = [];
        for (const row of displayedRows) {
            out.push(row);
            if (!expandedRows.includes(row.id) || !row.product?.sku) continue;
            const entry = historicalCache[row.id];
            out.push({
                id: `hist-${row.id}`,
                isHistoricalRow: true,
                panelDistributorId: row.distributorId,
                panelSku: row.product.sku,
                product: {
                    brandFamily: row.product?.brandFamily,
                    brand: row.product?.brand,
                    name: "Prior Year (Actual Depletions)",
                    sku: null,
                },
                distributorId: row.distributorId,
                fulfillmentMethodId: row.fulfillmentMethodId,
                forecastAmounts: [],
                depletions: entry?.depletions ?? [],
            });
        }
        return out;
    }, [displayedRows, expandedRows, historicalCache]);

    const handleCellKeyDown = (params, event) => {
        if (event.key !== 'Tab' || params.cellMode === "view") {
            return;
        }

        event.preventDefault();
        event.defaultMuiPrevented = true;

        const rowIds = gridSortedRowIdsSelector(apiRef.current.state);
        const columns = apiRef.current.getVisibleColumns();

        const nextCell = {
            rowIndex: rowIds.findIndex((id) => id === params.id),
            colIndex: apiRef.current.getColumnIndex(params.field),
        };

        let currColIndex = nextCell.colIndex;
        if (!event.shiftKey) {
            for (let i = 0; i < columns.length; i++) {
                if (columns[i].editable && nextCell.colIndex < i) {
                    nextCell.colIndex = i;
                    break;
                }
            }
            if (currColIndex === nextCell.colIndex) {
                nextCell.colIndex = findFirstEditableColumn(columns, 0);
                nextCell.rowIndex = findNextEditableRow(rowIds, nextCell.rowIndex);
            }
        } else {
            for (let i = columns.length - 1; i > 0; i--) {
                if (columns[i].editable && nextCell.colIndex > i) {
                    nextCell.colIndex = i;
                    break;
                }
            }
            if (currColIndex === nextCell.colIndex) {
                nextCell.colIndex = findLastEditableColumn(columns, 0);
                nextCell.rowIndex = findPreviousEditableRow(rowIds, nextCell.rowIndex);
            }
        }

        if (nextCell.rowIndex === -1) {
            // no applicable row is found, do nothing
            return;
        }

        const field = columns[nextCell.colIndex].field;
        const id = rowIds[nextCell.rowIndex];
        try {
            apiRef.current.setCellFocus(id, field);
            apiRef.current.startCellEditMode({ id, field });
            apiRef.current.scroll(nextCell);
            apiRef.current.scrollToIndexes(nextCell);
        } catch (e) {
            console.warn("Can't tab across.");
        }
    };

    const findFirstEditableColumn = (visibleColumns, currColId) => visibleColumns.findIndex((column, index) => index > currColId && column.editable);
    const findLastEditableColumn = (visibleColumns, currColId) => visibleColumns.findLastIndex((column, index) => index > currColId && column.editable);
    const findPreviousEditableRow = (rowIds, currRowId) => rowIds.findLastIndex((row, index) => index < currRowId && Number.isInteger(row));
    const findNextEditableRow = (rowIds, currRowId) => rowIds.findIndex((row, index) => index > currRowId && Number.isInteger(row));


    const isForecastAmountInputValid = (forecastAmountInput, originalForecastAmount) => {
        const inputMultiplierDifferenceFactor = 10;
        const maxForecastAmount = originalForecastAmount * inputMultiplierDifferenceFactor;
        const minForecastAmount = originalForecastAmount / inputMultiplierDifferenceFactor;

        if (forecastAmountInput == 0 && originalForecastAmount < 9) {
            return true;
        }

        if (forecastAmountInput >= maxForecastAmount || forecastAmountInput <= minForecastAmount) {
            return false;
        } else {
            return true;
        }
    };

    function getDepletionsColumns() {
        function matchDepletion(params, monthAdjustment) {
            let foundDepletion = params.row.depletions?.find((depletion) => dayjs(depletion.month, 'yyyyMM').month() === dayjs().subtract(monthAdjustment, "month").month(),);
            return foundDepletion?.amount;
        }

        return [{
            field: "depletions0",
            headerName: `${dayjs().subtract(3, "month").format("MMMM")}`,
            headerAlign: "center",
            width: 80,
            valueGetter: (params) => matchDepletion(params, 3),
        }, {
            field: "depletions1",
            headerName: `${dayjs().subtract(2, "month").format("MMMM")}`,
            headerAlign: "center",
            width: 80,
            valueGetter: (params) => matchDepletion(params, 2),
        }, {
            field: "depletions2",
            headerName: `${dayjs().subtract(1, "month").format("MMMM")}`,
            headerAlign: "center",
            width: 80,
            valueGetter: (params) => matchDepletion(params, 1),
        },];
    }

    const gridColumns = () => {
        let columnBuild = [
        ...(enablePauseIndicator ? [{
            field: "paused",
            headerName: "",
            headerAlign: "center",
            align: "center",
            width: 36,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                if (params.rowNode.type === "group") return null;
                return params.row.paused ? <PausedIndicator size={20} /> : null;
            },
        }] : []),
        {
            field: "product.brandFamily",
            headerName: "BrandFamily",
            width: 200,
            valueGetter: (params) => params.row.product?.brandFamily,
        }, {
            field: "product.name",
            headerName: "Product",
            width: 400,
            valueGetter: (params) => params.row.product?.name,
        }, {
            field: "product.brand",
            headerName: "Brand",
            width: 200,
            valueGetter: (params) => params.row.product?.brand,
        },
        ...(enableHistoricalSales ? [{
            field: "last4weeks",
            headerName: "",
            width: 36,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                if (!params.row.product?.sku) return null;
                const isExpanded = expandedRows.includes(params.id);
                return (
                    <Tooltip title="Historical Sales" arrow>
                        <IconButton
                            size="small"
                            sx={{ color: isExpanded ? "primary.dark" : "primary.main", p: 0 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setExpandedRows(prev =>
                                    prev.includes(params.id)
                                        ? prev.filter(id => id !== params.id)
                                        : [...prev, params.id]
                                );
                            }}
                        >
                            <InfoIcon sx={{ fontSize: 22 }} />
                        </IconButton>
                    </Tooltip>
                );
            },
        }] : []),
        {
            field: "distributorSapId",
            headerName: "Distributor",
            headerAlign: "center",
            width: 80,
            valueGetter: (params) => params.row.distributorSapId,
        }, {
            field: "product.sku",
            headerName: "BBC",
            headerAlign: "center",
            width: 80,
            valueGetter: (params) => params.row.product?.sku,
        }, {
            field: "customerPartNumber",
            headerName: "Customer",
            headerAlign: "center",
            width: 80,
            valueGetter: (params) => params.row.customerPartNumber,
        },];

        //Depletions Columns
        columnBuild = columnBuild.concat(getDepletionsColumns());

        //Forecast Columns
        for (let index = startWeek; index < endWeek; index++) {
            gridColumnsHelper(columnBuild, index);
        }

        return columnBuild;
    };

    const gridColumnsHelper = (columnBuild, index) => {
        let isWeekEditable = index >= editableWeeksStartIndex && index <= editableWeeksEndIndex;

        if (isHistorical) {
            columnBuild.push({
                field: `forecastAmount[${index}].depletion`,
                headerName: `D`,
                sortable: false,
                width: index <= editableWeeksEndIndex ? 60 : 135,
                type: "number",
                headerAlign: "center",
                align: index <= editableWeeksEndIndex ? "right" : "center",
                valueGetter: (params) => {
                    let depletion = matchDepletion(params, dayjs().add(index, "week").endOf("week"),);

                    if (isNaN(depletion?.amount)) {
                        return "";
                    }
                    return Math.round(depletion?.amount);
                },
            });
        }

        columnBuild.push({
            field: `forecastAmount[${index}].originalAmount`,
            headerName: `F`,
            sortable: false,
            width: index <= editableWeeksEndIndex ? 60 : 135,
            type: "number",
            headerAlign: "center",
            align: index <= editableWeeksEndIndex ? "right" : "center",
            valueGetter: (params) => {
                // The "Prior Year" row carries prior-year actual depletions, not a forecast. On the
                // Review page it is shown here under F; on the Report page the D column shows it instead.
                if (params.row?.isHistoricalRow) {
                    if (isHistorical) {
                        return "";
                    }
                    let depletion = matchDepletion(params, dayjs().add(index, "week").endOf("week"),);
                    return isNaN(depletion?.amount) ? "" : Math.round(depletion?.amount);
                }

                let foundForecast = matchForecast(params, dayjs().add(index, "week").endOf("week"),);

                if (!foundForecast || foundForecast.originalAmount === null || foundForecast.originalAmount === undefined || isNaN(foundForecast.originalAmount)) {
                    return "";
                }
                return Math.round(foundForecast.originalAmount);
            },
        });
        if (index <= editableWeeksEndIndex) {
            columnBuild.push({
                tabIndex: index,
                field: `forecastAmount[${index}].adjustedAmount`,
                headerName: `A`,
                sortable: false,
                width: 75,
                editable: isWeekEditable,
                type: "number",
                headerAlign: "center",
                valueGetter: (params) => {
                    let foundForecast = matchForecast(params, dayjs().add(index, "week").endOf("week"),);
                    if (foundForecast?.forecastAdjustmentId === 0 || isNaN(foundForecast?.adjustedAmount) || foundForecast?.adjustedAmount === null) {
                        return "";
                    }
                    return Math.round(foundForecast?.adjustedAmount);
                },
                valueSetter: (params) => {
                    if (params.row?.isHistoricalRow) {
                        return params.row;
                    }
                    let foundForecast = matchForecast(params, dayjs().add(index, "week").endOf("week"),);
                    let parsedInt = Math.round(parseFloat(params.value));

                    if (isInt(parsedInt) && parsedInt >= 0) {
                        // Valid integer value
                        if (foundForecast) {
                            foundForecast.adjustedAmount = parsedInt;
                        } else {
                            // Create new forecast object if it doesn't exist
                            const newForecast = {
                                weekEndDate: dayjs().add(index, "week").endOf("week").toDate(),
                                weekId: dayjs().add(index, "week").endOf("week").valueOf(),
                                adjustedAmount: parsedInt,
                                originalAmount: null, // This will be populated by the backend
                                forecastAdjustmentId: null
                            };
                            params.row.forecastAmounts.push(newForecast);
                        }
                    } else {
                        // Invalid or empty value - set to null
                        if (foundForecast) {
                            foundForecast.adjustedAmount = null;
                        } else {
                            // Create new forecast object with null adjustedAmount if it doesn't exist
                            const newForecast = {
                                weekEndDate: dayjs().add(index, "week").endOf("week").toDate(),
                                weekId: dayjs().add(index, "week").endOf("week").valueOf(),
                                adjustedAmount: null,
                                originalAmount: null, // This will be populated by the backend
                                forecastAdjustmentId: null
                            };
                            params.row.forecastAmounts.push(newForecast);
                        }
                    }
                    return params.row;
                },
            });
        }
    }

    const columnGroupingModelBuilder = (columnVisibilityModel, showHideDepletionsCallBack, anyDepletions,) => {
        let columnGrouping = [{
            groupId: "Name",
            description: "Name",
            headerAlign: "center",
            children: [{ field: "product.name" }],
        }, {
            groupId: "SKU",
            description: "SKU",
            headerAlign: "center",
            children: [{ field: "product.sku" }, { field: "customerPartNumber" }],
        },];


        columnGrouping.push({
            groupId: "Depletions",
            description: "Depletions",
            headerAlign: "center",
            children: [{ field: "depletions0" }, { field: "depletions1" }, { field: "depletions2" },],
        }, {
            groupId: "depletions",
            description: "Forecast Depletions",
            headerAlign: "left",
            renderHeaderGroup: () => (<div
                className={"MuiDataGrid-columnHeaderTitle css-t89xny-MuiDataGrid-columnHeaderTitle"}
            >
                <Stack direction={"horizontal"} alignItems={"center"}>
                    {anyDepletions && !isHistorical ? (
                        <>
                            <Button
                                variant="outlined"
                                sx={{ marginRight: 5 }}
                                onClick={() => {
                                    showHideDepletionsCallBack();
                                }}
                            >
                                <ChevronLeftIcon />
                                {`${columnVisibilityModel.depletions0 ? "Hide" : "Show"} Depletions`}
                            </Button>
                            <Typography variant={"h5"}>
                                <strong>
                                    (F) Depletions Forecast & (A) Customer Adjustments (Units)
                                </strong>
                            </Typography>
                        </>
                    ) : null}
                </Stack>
            </div>),
            children: [],
        });

        for (let x = startWeek; x < endWeek; x++) {
            columnGroupingBuilderHelper(columnGrouping, x);
        }

        return columnGrouping;
    };

    const columnGroupingBuilderHelper = (columnGrouping, index) => {
        columnGrouping[3].children.push({
            groupId: dayjs()
                .add(7 * index, "day")
                .endOf("week")
                .format("MM/DD/YY"),
            headerAlign: "center",
            children: [{ field: `forecastAmount[${index}].originalAmount` }, { field: `forecastAmount[${index}].adjustedAmount` },],
        });
    };

    if (areForecastReviewItemsReady) {
        let anyDepletions = displayedRows.some((item) => item.depletions !== undefined && item.depletions.some((x) => !!x),);
        columnDefinitions = gridColumns();
        columnGroupingModel = columnGroupingModelBuilder(columnVisibilityModel, showHideDepletions, anyDepletions,);
    }

    return (<div>
        {isReadOnly && !hideAlerts ? (<Stack direction='row' alignItems='center' justifyContent='center'>
            <Alert variant="filled" severity="info" sx={{ alignItems: 'center' }}>
                {selectedDistributors.length > 1 ? <Typography variant="h6">One of the selected distributors uses an automated system for forecast
                    adjustment. This view is read only.</Typography> : <Typography variant="h6">The selected distributor uses an automated system for forecast
                        adjustment. This view is read only.</Typography>}
            </Alert>
        </Stack>) : null}
        {error ? (<Alert
            variant="filled"
            severity="error"
            action={<Button
                color="inherit"
                size="small"
                onClick={() => window.location.reload()}
            >
                Refresh Page
            </Button>}
        >
            Updates have failed! Refresh the page before trying again.
        </Alert>) : (<Box
            sx={{
                height: height ?? "calc(100vh - 350px)",
                width: "100%",
                padding: height ? 0 : 2,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                "& .adjustable": {
                    backgroundColor: "#d2f7d4", color: "#000000",
                }, "& .adjusted": {
                    backgroundColor: "#c1f7ec", color: "#000000",
                }, "& .banded": {
                    backgroundColor: "#a9dbfc", color: "#000000",
                }, "& .locked": {
                    backgroundColor: "#EBEBE4", color: "#000000",
                }, "& .borderRight": {
                    borderRight: "1px solid black !important",
                }, "& .historicalRow": {
                    fontWeight: "bold", fontStyle: "italic",
                },
                "& .MuiDataGrid-root": { flex: 1, minHeight: 0 },
            }}
        >
            {!hideAlerts && <Alert style={{ marginBottom: 8 }} severity="warning">Note that the forecasted quantity displayed is representative of the forecast for both direct and any partnered indirect wholesalers. {!isHistorical && "Please ensure any forecast adjustments also account for indirect wholesalers."}</Alert>}
            {isHistorical &&
                <Typography textAlign={"center"} variant={"h5"}>
                    <strong>
                        (D) Depletions & (F) Depletions Forecast & (A) Customer Adjustments (Units)
                    </strong>
                </Typography>}
            <DataGridPremium
                hideFooter
                rows={rowsWithHistorical}
                columns={columnDefinitions}
                apiRef={apiRef}
                loading={error || !areForecastReviewItemsReady || sending}
                groupingColDef={{
                    leafField: "product.name",
                    renderCell: (params) => {
                        if (params.rowNode.type !== "group") {
                            return params.formattedValue ?? params.value;
                        }
                        const { id, groupingKey, childrenExpanded, depth } = params.rowNode;
                        const childCount = params.rowNode.children?.length ?? 0;
                        const arrow = childrenExpanded ? "\u25BC" : "\u25B6";
                        const onToggle = (event) => {
                            event.stopPropagation();
                            apiRef.current.setRowChildrenExpansion(id, !childrenExpanded);
                        };
                        const label = `${arrow} ${groupingKey} (${childCount})`;
                        const style = { cursor: "pointer", paddingLeft: depth * 16 };
                        if (depth === 0) {
                            return (
                                <span onClick={onToggle} style={style}>
                                    <strong>{label}</strong>
                                </span>
                            );
                        }
                        return (
                            <span onClick={onToggle} style={style}>
                                {label}
                            </span>
                        );
                    },
                }}
                initialState={initialState}
                columnGroupingModel={columnGroupingModel}
                columnVisibilityModel={columnVisibilityModel}
                disableColumnMenu
                isGroupExpandedByDefault={(node) => expandedGroups.current.includes(node.id)}
                rowHeight={30}
                columnHeaderHeight={30}
                experimentalFeatures={{
                    columnGrouping: true, newEditingApi: true,
                }}
                isCellEditable={(params) => {
                    if (params.row?.isHistoricalRow) {
                        return false;
                    }

                    if (isReadOnly) {
                        return false;
                    }

                    let index = parseInt(params.field.substring(params.field.indexOf("[") + 1, params.field.length - params.field.indexOf("]"),),);

                    if (isNaN(index)) {
                        return false;
                    }

                    let foundForecast = matchForecast(params, dayjs().add(index, "week").endOf("week"),);

                    if (!foundForecast) return false;

                    return foundForecast && foundForecast.originalAmount !== null && foundForecast.originalAmount !== undefined && !isNaN(parseInt(foundForecast.originalAmount));
                }}
                onCellEditStart={(params, event) => {
                    if (event.type != "dblclick") {
                        event.preventDefault();
                        event.defaultMuiPrevented = true;
                    }
                }}
                {...(enableHistoricalSales ? {
                    // Panel attaches to the injected "Prior Year" rows (hist-<id>), so it renders below
                    // that row. Toggling is driven by the info button via expandedRows, not this handler.
                    detailPanelExpandedRowIds: expandedRows.map((id) => `hist-${id}`),
                    onDetailPanelExpandedRowIdsChange: () => { },
                    getDetailPanelContent: (params) =>
                        params.row.isHistoricalRow && params.row.panelSku ? (
                            <YearOverYearSalesDetailPanel
                                distributorId={params.row.panelDistributorId}
                                sku={params.row.panelSku}
                            />
                        ) : null,
                    getDetailPanelHeight: () => "auto",
                } : {})}
                onCellKeyDown={handleCellKeyDown}
                // eslint-disable-next-line no-unused-vars
                onCellEditStop={(params, event) => {
                    setTimeout(() => {
                        // validate changed input
                        const forecastId = params.field.split("[")[1].split("]")[0];
                        const foundForecast = params.row.forecastAmounts[forecastId];
                        if (foundForecast && foundForecast.originalAmount != null && foundForecast.adjustedAmount != null &&
                            !isForecastAmountInputValid(foundForecast.adjustedAmount, foundForecast.originalAmount)) {
                            alert("Тhe adjustment made is +/- 10x than the current forecast.  Please verify this adjustment is correct.");
                        }
                    }, 150);
                    // show error if leaving the page with unsubmitted data
                    setEdited(true);
                }}
                slotProps={{
                    cell: {
                        onFocus: event => {
                            if (event.target?.tagName.toLowerCase() === "input") {
                                event.target?.select();
                            }
                        }
                    }
                }}
                getRowClassName={(params) => (params.row?.isHistoricalRow ? "historicalRow" : "")}
                getCellClassName={(params) => {
                    if (params.row?.isHistoricalRow) {
                        return params.field.includes("adjusted") ? "borderRight locked" : "locked";
                    }

                    let index = parseInt(params.field.substring(15, 17));
                    if (index < editableWeeksStartIndex || index > editableWeeksEndIndex) {
                        let className = "";
                        if (params.field.includes("adjusted")) {
                            className += "borderRight ";
                        }
                        return className + "locked ";
                    }

                    return params.field.includes("adjusted") ? isInt(params.value) || params.value === " " ? "adjustable" : "adjusted" : " ";
                }}
            />
            {!isHistorical && !hideSubmit && <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-start"
                spacing={2}
                sx={{ pt: 2 }}
            >
                <Button
                    variant="contained"
                    onClick={async () => {
                        setSending(true);
                        await SaveForecastAdjustmentChanges();
                        setSending(false);
                        setEdited(false);
                        setSelectedProducts([]);
                    }}
                    disabled={!edited}
                >
                    Submit
                </Button>
                <Typography variant="body">
                    I acknowledge and agree that BBC will generate shipments based
                    upon reported wholesaler inventory and forecasted depletions,
                    subject to change pending transportation and product
                    availability.
                </Typography>
                {sending && <LinearProgress size={24} />}
            </Stack>}
        </Box>)}
    </div>);
}

ForecastReviewGrid.propTypes = {
    editableWeeksStartIndex: PropTypes.number,
    editableWeeksEndIndex: PropTypes.number,
    startWeek: PropTypes.number,
    endWeek: PropTypes.number,
    isHistorical: PropTypes.bool,
    filterByFulfillmentMethodId: PropTypes.number,
    hideSubmit: PropTypes.bool,
    hideAlerts: PropTypes.bool,
    height: PropTypes.string,
}
